import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, TrendingUp, DollarSign, Clock, Shield, Target, CheckCircle, Map, Bell, Bookmark, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ForInvestorsPage() {
  const benefits = [
    {
      icon: Search,
      title: 'Access Off-Market Deals First',
      description: 'See exclusive investment properties before they hit the MLS. Our network of wholesalers and deal posters bring you opportunities your competition will never find.'
    },
    {
      icon: Map,
      title: 'Interactive Map Search',
      description: 'Visualize deals across your target markets with our powerful map view. Zoom into neighborhoods and discover opportunities in real-time.'
    },
    {
      icon: TrendingUp,
      title: 'Built-in Deal Analysis',
      description: 'Every listing includes ARV estimates, rehab costs, and projected ROI. Make data-driven decisions without the spreadsheet headaches.'
    },
    {
      icon: Bell,
      title: 'Instant Deal Alerts',
      description: 'Save your search criteria and get notified the moment a matching deal hits the platform. Never miss another opportunity.'
    },
    {
      icon: Bookmark,
      title: 'Save & Track Deals',
      description: 'Bookmark properties, compare options side-by-side, and track your pipeline from one centralized dashboard.'
    },
    {
      icon: Users,
      title: 'Vetted Service Providers',
      description: 'Connect with investor-friendly realtors, contractors, lenders, and title companies who understand the game.'
    }
  ];

  const painPoints = [
    'Tired of competing with 50 other investors for the same MLS deal?',
    'Frustrated by contractors who ghost you mid-project?',
    'Wasting hours analyzing deals that don\'t pencil out?',
    'Missing opportunities because you heard about them too late?'
  ];

  const dealTypes = [
    'Wholesale Assignments',
    'Fix & Flip Opportunities',
    'Buy & Hold Rentals',
    'Turnkey Properties',
    'Foreclosures & REOs',
    'Multi-Family Buildings'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7578939/pexels-photo-7578939.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Real estate investor analyzing deals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#5DADE2]/20 border border-[#5DADE2]/30 text-[#5DADE2] text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Target className="h-4 w-4" />
              For Real Estate Investors
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="investor-hero-title">
              Find Off-Market Deals Before Your Competition
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Stop fighting over overpriced MLS listings. NextFlip connects you directly with wholesalers and deal posters who have what you're looking for — properties with real margins.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8 text-lg h-14" data-testid="investor-signup-btn">
                <Link to="/signup">
                  Start Finding Deals
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/marketplace">
                  Browse Deals
                </Link>
              </Button>
            </div>

            <p className="text-slate-400 text-sm">
              Free to join. No credit card required. Start browsing in 30 seconds.
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Sound Familiar?
            </h2>
            <div className="space-y-4">
              {painPoints.map((point, i) => (
                <p key={i} className="text-lg text-slate-600 flex items-start gap-3 text-left">
                  <span className="text-red-500 mt-1">✗</span>
                  {point}
                </p>
              ))}
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-heading font-semibold text-slate-900 mb-2">
              There's a better way.
            </p>
            <p className="text-lg text-[#5DADE2] font-medium">
              NextFlip was built by investors, for investors.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Find Your Next Deal
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Purpose-built tools that save you time, surface better opportunities, and help you close more deals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-[#5DADE2]/50 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deal Types Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Find Every Type of Investment Deal
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Whether you're a fix-and-flipper, buy-and-hold investor, or looking for turnkey rentals, our marketplace has deals that match your strategy.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {dealTypes.map((type, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#5DADE2] flex-shrink-0" />
                    <span className="text-white">{type}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7031406/pexels-photo-7031406.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Investment properties"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Avg. Investor ROI</p>
                    <p className="text-2xl font-bold text-slate-900">23%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Start Finding Deals in Minutes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Create Your Free Account', desc: 'Sign up in 30 seconds. Tell us about your investment criteria and target markets.' },
              { step: '02', title: 'Browse Off-Market Deals', desc: 'Search our marketplace by location, price, property type, and deal strategy.' },
              { step: '03', title: 'Connect & Close', desc: 'Contact deal posters directly and close deals faster than your competition.' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#5DADE2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-[#5DADE2]">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#5DADE2] to-blue-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Your Next Investment Property is Waiting
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of investors who find off-market deals on NextFlip every day. It's free to sign up and takes less than a minute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-slate-100 rounded-full px-8 text-lg h-14">
              <Link to="/signup">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/marketplace">
                Browse Deals First
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
